import { useEffect, useState } from "react";
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Drawer,
    IconButton,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import api from "../../../../utils/api";
import { _date, formatMoney } from "../../../../utils/helpers";

export default function SalaryDetailsDrawer({ open, group = null, onClose, onChanged }) {
    const [rows, setRows] = useState([]);
    const [meta, setMeta] = useState({ lessons_count: 0, total_amount: 0 });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [deletingId, setDeletingId] = useState(null);

    const loadDetails = async () => {
        if (!group?.teacher_id || !group?.period_month) return;
        setLoading(true);
        setError("");
        try {
            const res = await api.get("admin/income-expenses/salary/groups/details", {
                params: {
                    teacher_id: group.teacher_id,
                    period: group.period_month,
                },
            });
            setRows(res.data?.data || []);
            setMeta(res.data?.meta || { lessons_count: 0, total_amount: 0 });
        } catch (err) {
            setError(err.response?.data?.message || "Failed to load salary details.");
            setRows([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (open) {
            loadDetails();
        } else {
            setRows([]);
            setError("");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, group?.teacher_id, group?.period_month]);

    const removeEntry = async (entryId) => {
        if (!entryId) return;
        if (!window.confirm("Delete this salary entry?")) return;
        setDeletingId(entryId);
        try {
            await api.delete(`admin/income-expenses/${entryId}`);
            setRows((prev) => prev.filter((r) => Number(r.entry_id) !== Number(entryId)));
            onChanged?.();
        } catch (err) {
            window.alert(err.response?.data?.message || "Could not delete entry.");
        } finally {
            setDeletingId(null);
        }
    };

    return (
        <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{ sx: { width: { xs: "100%", md: 720 } } }}>
            <Box sx={{ p: 2, display: "flex", flexDirection: "column", height: "100%" }}>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                    <Box>
                        <Typography variant="h6">{group?.teacher_name || "Salary details"}</Typography>
                        <Typography variant="body2" color="text.secondary">
                            {group?.period_month || "—"} • {meta.lessons_count} lessons • {formatMoney(Number(meta.total_amount || 0))}
                        </Typography>
                    </Box>
                    <IconButton onClick={onClose} aria-label="Close">
                        <CloseIcon />
                    </IconButton>
                </Box>

                {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}

                {loading ? (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <Box sx={{ overflow: "auto", flex: 1 }}>
                        <Table size="small" stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Lesson</TableCell>
                                    <TableCell>Classroom</TableCell>
                                    <TableCell>Type</TableCell>
                                    <TableCell>Date</TableCell>
                                    <TableCell align="center">Duration</TableCell>
                                    <TableCell align="right">Amount</TableCell>
                                    <TableCell align="center">Status</TableCell>
                                    <TableCell align="right">Action</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {rows.map((row) => (
                                    <TableRow key={row.entry_id} hover>
                                        <TableCell>#{row.lesson_id}</TableCell>
                                        <TableCell>{row.classroom_name || "—"}</TableCell>
                                        <TableCell>{row.lesson_type_name || "—"}</TableCell>
                                        <TableCell>
                                            <Typography variant="body2">
                                                {_date(row.starts_at, { relative: false, long: false })}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="center">{row.duration_minutes || 0} min</TableCell>
                                        <TableCell align="right">
                                            <Typography variant="body2">
                                                {formatMoney(Number(row.amount || 0))}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="center">
                                            <Stack direction="column" spacing={0.5} alignItems="center">
                                                <Chip size="small" label={row.status} variant="outlined" />
                                                <Chip size="small" label={row.lesson_status} color="default" />
                                            </Stack>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Tooltip title="Delete this entry">
                                                <span>
                                                    <IconButton
                                                        size="small"
                                                        color="error"
                                                        onClick={() => removeEntry(row.entry_id)}
                                                        disabled={deletingId === row.entry_id}
                                                    >
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {!rows.length ? (
                                    <TableRow>
                                        <TableCell colSpan={8} align="center">
                                            <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                                                No lessons in this group.
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : null}
                            </TableBody>
                        </Table>
                    </Box>
                )}

                <Box sx={{ pt: 2, borderTop: "1px solid", borderColor: "divider", mt: 2 }}>
                    <Button onClick={onClose}>Close</Button>
                </Box>
            </Box>
        </Drawer>
    );
}
